// Latest podcast episodes from the show's public RSS feed, rendered into [data-ltl-podcast-feed].
// Progressive enhancement: the section's server-rendered content (platform links, any manual
// episode blocks) stays exactly as it is until the feed has loaded and produced at least one
// usable episode. Any failure (network, timeout, malformed XML, no usable items) leaves the page
// untouched and shows no error. Feed text is only ever inserted with textContent, never as HTML.
//
// Listen links: an episode links to its own open.spotify.com page when the feed's
// podcast:transcript URL carries the Spotify episode ID for the configured show; otherwise it
// links to the Spotify show page. The feed's own <link> (Spotify for Creators) is not used.
(() => {
  const NS = {
    itunes: 'http://www.itunes.com/dtds/podcast-1.0.dtd',
    podcast: 'https://podcastindex.org/namespace/1.0',
  };
  const TIMEOUT_MS = 8000;
  const CACHE_MS = 30 * 60 * 1000;
  const EXCERPT_CHARS = 180;
  const FIRST_SENTENCE_MAX = 320;
  const ABBREVIATIONS = new Set(['dr', 'mr', 'mrs', 'ms', 'st', 'jr', 'sr', 'vs', 'ft', 'pt', 'no', 'mt']);
  const SPOTIFY_ID = /^[A-Za-z0-9]{22}$/;

  const text = (node) => (node && node.textContent ? node.textContent.trim() : '');
  const firstNS = (item, ns, name) => item.getElementsByTagNameNS(ns, name)[0] || null;

  const plainText = (value) => {
    if (!value) return '';
    const doc = new DOMParser().parseFromString(value, 'text/html');
    doc.querySelectorAll('script, style, noscript, template').forEach((el) => el.remove());
    return (doc.body ? doc.body.textContent : '').replace(/\s+/g, ' ').trim();
  };

  // Sentences of a plain-text passage, not split after common abbreviations (Dr., Ft., St., …).
  const sentences = (value) => {
    const out = [];
    let current = '';
    // Feeds sometimes drop the space between sentences ("Investigation.Dr. Kaylor"); restore it.
    const spaced = value.replace(/([a-z0-9)][.!?])(?=[A-Z])/g, '$1 ');
    for (const piece of spaced.split(/(?<=[.!?])\s+/)) {
      current = current ? `${current} ${piece}` : piece;
      const lastWord = current.replace(/[.!?]+$/, '').split(/\s+/).pop().replace(/^[("'“]+/, '').toLowerCase();
      if (/\.$/.test(current) && ABBREVIATIONS.has(lastWord)) continue;
      out.push(current);
      current = '';
    }
    if (current) out.push(current);
    return out;
  };

  // Opening of a passage ending on a complete sentence, never an ellipsis: the first sentence
  // (dropped entirely if it is extremely long) plus following sentences within `max` characters.
  const excerpt = (value, max = EXCERPT_CHARS) => {
    const [first, ...rest] = sentences(value);
    if (!first || first.length > FIRST_SENTENCE_MAX) return '';
    let out = first;
    for (const sentence of rest) {
      if (out.length + 1 + sentence.length > max) break;
      out = `${out} ${sentence}`;
    }
    return out;
  };

  // "01:04:43", "54:40", "3600" (seconds) -> { h, m }; anything else -> null.
  const parseDuration = (value) => {
    const raw = (value || '').trim();
    if (!raw) return null;
    let seconds;
    if (/^\d+$/.test(raw)) {
      seconds = Number(raw);
    } else if (/^\d{1,2}(:\d{1,2}){1,2}$/.test(raw)) {
      seconds = raw.split(':').map(Number).reduce((total, part) => total * 60 + part, 0);
    } else {
      return null;
    }
    if (!Number.isFinite(seconds) || seconds <= 0) return null;
    const minutes = Math.round(seconds / 60);
    return { h: Math.floor(minutes / 60), m: minutes % 60 };
  };

  const formatDuration = (duration, labels) => {
    if (!duration) return '';
    const parts = [];
    if (duration.h) parts.push(`${duration.h} ${labels.hour}`);
    if (duration.m || !duration.h) parts.push(`${duration.m || 1} ${labels.minute}`);
    return parts.join(' ');
  };

  const formatDate = (date) =>
    date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric', timeZone: 'UTC' });

  const spotifyShowId = (url) => {
    const match = /open\.spotify\.com\/show\/([A-Za-z0-9]{22})/.exec(url || '');
    return match ? match[1] : '';
  };

  const listenUrl = (item, showId, showUrl) => {
    const transcript = firstNS(item, NS.podcast, 'transcript');
    const match = /spotifycdn\.com\/([A-Za-z0-9]{22})\/([A-Za-z0-9]{22})\//.exec(
      transcript ? transcript.getAttribute('url') || '' : ''
    );
    if (match && showId && match[1] === showId && SPOTIFY_ID.test(match[2])) {
      return `https://open.spotify.com/episode/${match[2]}`;
    }
    return showUrl || '';
  };

  // Pure: XML string -> newest-first published episodes (at most `count`). Throws on bad XML.
  const parseFeed = (xml, { count = 6, showUrl = '', now = Date.now() } = {}) => {
    const doc = new DOMParser().parseFromString(xml, 'application/xml');
    if (doc.getElementsByTagName('parsererror').length || !doc.getElementsByTagName('channel').length) {
      throw new Error('invalid feed');
    }
    const showId = spotifyShowId(showUrl);
    const episodes = [];
    for (const item of Array.from(doc.getElementsByTagName('item'))) {
      const title = text(item.getElementsByTagName('title')[0]);
      const published = new Date(text(item.getElementsByTagName('pubDate')[0]));
      const url = listenUrl(item, showId, showUrl);
      if (!title || Number.isNaN(published.getTime()) || published.getTime() > now || !/^https:\/\//.test(url)) {
        continue;
      }
      const summary =
        text(item.getElementsByTagName('description')[0]) || text(firstNS(item, NS.itunes, 'summary'));
      episodes.push({
        title,
        date: published.toISOString(),
        duration: parseDuration(text(firstNS(item, NS.itunes, 'duration'))),
        description: excerpt(plainText(summary)),
        url,
        episodeLink: url !== showUrl,
      });
    }
    episodes.sort((a, b) => (a.date < b.date ? 1 : a.date > b.date ? -1 : 0));
    return episodes.slice(0, count);
  };

  const cacheKey = (url) => `ltl-podcast-feed:${url}`;

  const readCache = (url) => {
    try {
      const cached = JSON.parse(sessionStorage.getItem(cacheKey(url)) || 'null');
      if (cached && Date.now() - cached.t < CACHE_MS && Array.isArray(cached.episodes)) return cached.episodes;
    } catch (error) {
      // Storage unavailable (private mode, blocked): fetch instead.
    }
    return null;
  };

  const writeCache = (url, episodes) => {
    try {
      sessionStorage.setItem(cacheKey(url), JSON.stringify({ t: Date.now(), episodes }));
    } catch (error) {
      // Storage unavailable or full: nothing to do.
    }
  };

  const fetchFeed = async (url) => {
    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), TIMEOUT_MS);
    try {
      const response = await fetch(url, { signal: controller.signal, credentials: 'omit' });
      if (!response.ok) throw new Error(`feed ${response.status}`);
      return await response.text();
    } finally {
      clearTimeout(timer);
    }
  };

  const render = (root, episodes) => {
    const list = root.querySelector('[data-episodes-feed]');
    const template = root.querySelector('template[data-episode-template]');
    if (!list || !template) return false;
    const labels = {
      hour: root.dataset.hourLabel || 'hr',
      minute: root.dataset.minuteLabel || 'min',
      latest: root.dataset.latestLabel || '',
      listen: root.dataset.listenLabel || 'Listen',
    };
    const fragment = document.createDocumentFragment();
    episodes.forEach((episode, index) => {
      const node = template.content.firstElementChild.cloneNode(true);
      const meta = [formatDate(new Date(episode.date)), formatDuration(episode.duration, labels)].filter(Boolean);
      if (index === 0 && labels.latest) meta.unshift(labels.latest);
      node.querySelector('[data-episode-meta]').textContent = meta.join(' · ');
      node.querySelector('[data-episode-title]').textContent = episode.title;
      const description = node.querySelector('[data-episode-description]');
      if (episode.description) description.textContent = episode.description;
      else description.remove();
      const link = node.querySelector('a');
      link.href = episode.url;
      link.setAttribute('aria-label', `${labels.listen}: ${episode.title}`);
      const label = link.querySelector('span');
      if (label) label.textContent = labels.listen;
      fragment.appendChild(node);
    });
    list.replaceChildren(fragment);
    list.hidden = false;
    root.querySelectorAll('[data-episodes-manual]').forEach((el) => {
      el.hidden = true;
    });
    root.querySelectorAll('[data-episodes-header]').forEach((el) => {
      el.hidden = false;
    });
    root.dataset.feedState = 'ready';
    return true;
  };

  const init = async (root) => {
    const url = root.dataset.feedUrl;
    if (!url || !/^https:\/\//.test(url) || root.dataset.feedState) return;
    root.dataset.feedState = 'loading';
    const options = { count: Number(root.dataset.count) || 6, showUrl: root.dataset.spotifyUrl || '' };
    try {
      let episodes = readCache(url);
      if (!episodes) {
        episodes = parseFeed(await fetchFeed(url), options);
        if (episodes.length) writeCache(url, episodes);
      }
      if (!episodes.length || !render(root, episodes.slice(0, options.count))) {
        root.dataset.feedState = 'fallback';
      }
    } catch (error) {
      root.dataset.feedState = 'fallback';
    }
  };

  window.LtlPodcastFeed = { parseFeed, parseDuration, formatDuration, excerpt, plainText };

  const start = () => document.querySelectorAll('[data-ltl-podcast-feed]').forEach(init);
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start);
  else start();
})();
